import React, { useState, useEffect } from "react";

import "./product.css";

import {
  addDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

import { toast } from "react-toastify";

import { handleImageChange, handleImageSubmit } from "../../Utility/utils";

import { db } from "../../Config/Firebase/config";

const ProductAdminForm = () => {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [file, setFile] = useState(null);
  const [price, setPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [available, setAvailable] = useState(true);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [formKey, setFormKey] = useState(0);
  const [uploading, setUploading] = useState(false);

  // FETCH CATEGORIES
  useEffect(() => {
    const CACHE_KEY = "categoryCache";
    const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000;

    const fetchCategories = async () => {
      try {
        const categoryCollectionRef = collection(db, "category");

        const data = await getDocs(categoryCollectionRef);

        const categoriesData = data.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCategories(categoriesData);

        const currentTime = new Date().getTime();

        const cachedData = {
          data: categoriesData,
          timestamp: currentTime,
        };

        localStorage.setItem(CACHE_KEY, JSON.stringify(cachedData));
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      }
    };

    try {
      const cachedData = localStorage.getItem(CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);

        const currentTime = new Date().getTime();

        if (
          Array.isArray(data) &&
          currentTime - timestamp <= CACHE_EXPIRATION
        ) {
          setCategories(data);
        } else {
          fetchCategories();
        }
      } else {
        fetchCategories();
      }
    } catch (error) {
      console.error("Error reading category cache:", error);
      fetchCategories();
    }
  }, []);

  // ADD PRODUCT
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (title.trim() === "") {
      toast.warning("Please enter product title");
      return;
    }

    if (!file) {
      toast.warning("Please select a product image");
      return;
    }

    if (category.trim() === "") {
      toast.warning("Please select a category");
      return;
    }

    if (price.trim() === "") {
      toast.warning("Please enter product price");
      return;
    }

    if (costPrice.trim() === "") {
      toast.warning("Please enter product cost price");
      return;
    }

    try {
      setUploading(true);

      // Upload product image to Cloudinary
      const downloadURL = await handleImageSubmit(
        file,
        setFile,
        () => {},
        "lts/product",
      );

      if (!downloadURL) {
        throw new Error("Product image upload failed");
      }

      // Save product to Firestore
      await addDoc(collection(db, "product"), {
        title: title.trim(),
        details: details.trim(),
        image: downloadURL,
        price,
        costPrice,
        available,
        category,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Product added successfully!");

      // Reset form
      setTitle("");
      setDetails("");
      setFile(null);
      setPrice("");
      setCostPrice("");
      setAvailable(true);
      setCategory("");

      setFormKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  const onImageChange = (event) => {
    handleImageChange(event, setFile);
  };

  return (
    <div className="product-admin-container">
      <div className="product-admin-heading">
        <h3>Add Product</h3>
      </div>

      <div className="product-admin-form">
        <form onSubmit={handleSubmit} key={formKey}>
          <div className="form-group">
            <label htmlFor="category">Category:</label>

            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select Category</option>

              {categories.map((cat) => (
                <option key={cat.id} value={cat.title}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title:</label>

            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="details">Product Details:</label>

            <textarea
              id="details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows="5"
              placeholder="Enter product details..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="price">Price:</label>

            <input
              type="string"
              id="price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="costPrice">Cost Price:</label>

            <input
              type="string"
              id="costPrice"
              value={costPrice}
              onChange={(e) => setCostPrice(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="file">Choose Image:</label>

            <input
              type="file"
              id="file"
              accept="image/*"
              onChange={onImageChange}
              required
            />
          </div>

          <button className="btn" type="submit" disabled={uploading}>
            {uploading ? "Uploading..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductAdminForm;
